import React from 'react';
import { AlertCircle, Clock, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { ActiveRunsErrorResponse } from '../../types/api';

interface ActiveRunsDialogProps {
  /**
   * Whether the dialog is visible
   */
  isOpen: boolean;
  
  /**
   * The active runs error data from the API
   */
  errorData: ActiveRunsErrorResponse | null;
  
  /**
   * Called when user chooses to wait for the current response
   */
  onWait: () => void;
  
  /**
   * Called when user chooses to cancel the current response
   */
  onCancel: () => void;
  
  /**
   * Whether a cancel operation is in progress
   */
  isCancelling?: boolean;
  
  /**
   * Called to close the dialog without action
   */
  onClose: () => void;
}

/**
 * Dialog shown when user tries to send a message while AI is still processing
 * Offers options to wait or cancel the current response
 */
export const ActiveRunsDialog: React.FC<ActiveRunsDialogProps> = ({
  isOpen,
  errorData,
  onWait,
  onCancel,
  isCancelling = false,
  onClose
}) => {
  if (!isOpen || !errorData) return null;

  const activeRunCount = errorData.activeRuns?.length || 1;
  const canCancel = errorData.canCancel !== false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              AI is Still Processing
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-gray-600 mb-4">
            {errorData.message || 'The AI is still processing your previous message. You can wait for it to complete or cancel it to send a new message.'}
          </p>
          
          {/* Active runs info */}
          {activeRunCount > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>
                  {activeRunCount} active {activeRunCount === 1 ? 'process' : 'processes'} running
                </span>
              </div>
              {errorData.activeRuns && errorData.activeRuns.length > 0 && (
                <div className="mt-2 space-y-1">
                  {errorData.activeRuns.map((run, index) => (
                    <div key={run.id || index} className="text-xs text-gray-500 flex items-center gap-2">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Status: {run.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Options */}
          <div className="space-y-3">
            {/* Wait option */}
            <div className="p-3 border border-green-200 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-800 mb-1">
                Wait for Response
              </h4>
              <p className="text-sm text-green-700">
                {errorData.actions?.wait?.description || 'Wait for the current response to complete before sending a new message.'}
              </p>
              <p className="text-xs text-green-600 mt-1 italic">
                {errorData.actions?.wait?.recommendation || 'Recommended for preserving conversation flow.'}
              </p>
            </div>

            {/* Cancel option */}
            {canCancel && (
              <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-1">
                  Cancel Current Response
                </h4>
                <p className="text-sm text-amber-700">
                  {errorData.actions?.cancel?.description || 'Cancel the current AI response and send a new message.'}
                </p>
                {errorData.actions?.cancel?.warning && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠️ {errorData.actions.cancel.warning}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
          <Button
            variant="primary"
            onClick={onWait}
            className="flex-1"
          >
            <Clock className="w-4 h-4 mr-2" />
            Wait
          </Button>
          
          {canCancel && (
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isCancelling}
              className="flex-1"
            >
              {isCancelling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Cancel Response
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActiveRunsDialog;
